import { Button, showAlert } from '@beehive/components';
import React, { useRef, useState } from 'react';
import './index.less';
import Template from '../components/Template';
import { NodeEnum, getListPathMenuKey } from 'containers/OutBidding/utils';
import { transformToBlobIds } from '../utils';
import isBlank from 'utils/object';
import _ from 'lodash';
import BiddingAgreeCheckbox from '@mpc/common/src/features/Bidding/BiddingAgreeCheckbox';
import { Modal } from '@bedrock/components';
import { useMemoizedFn } from 'ahooks';
import { outsourceCommonDeleteTemporary } from '@mpc/common/src/services/api';
import { getVendorStorage } from '@mpc/common/src/utils';

/**
 * 待提交标书
 */
const AwaitingSubmissionBids = (props: any) => {
  const { detail = {}, editFields = [], getDetail, projectMove, quoteData } = props;
  const [loading, setLoading] = useState(false);

  const { permit_actions = {} } = detail;
  const canMove = permit_actions.can_move;
  const hasSubmitted = !!canMove && !!detail?.bidding_suppliers?.[0].bidding_supplier_quotes?.[0]?.created_at;
  const [agree, setAgree] = useState(false);
  const agreeCheckBoxRef = useRef<any>(null);

  // 兼容适配：详情接口 structured 字段（1 为新结构化报价，0 为旧非结构化报价）
  const isStructured = (detail?.structured ?? 0) === 1;

  const handleSubmit = async (validData: any) => {
    try {
      // structured=1 时 validData 为 async 需 await；structured=0 时 validData 为同步调用
      const params =  await validData();
      if (params) {
        setLoading(true);
        const { evaluationValues, quoteFormValues } = params ?? {};
        const { tender_plan_attach_blob_ids } = evaluationValues ?? {};
        const { bidding_suppliers = [] } = detail;
        const [currentSupplier] = bidding_suppliers;
        const { bidding_supplier_quotes = [] } = currentSupplier;
        const [currentQuote = {}] = bidding_supplier_quotes;

        // structured=1：对齐后端 AwaitingSubmissionBidsMoveBiddingDto 结构（含 bidding_supplier, tabs）
        // structured=0：对齐历史接口，仅传报价核心字段
        const data = {
          bidding_supplier_id: detail.current_bidding_supplier_id,
          bidding_supplier_quote_dtos: [
            _.omitBy(
              isStructured
                ? {
                    id: isBlank(currentQuote.id) ? undefined : currentQuote.id,
                    tender_plan_attach_blob_ids: transformToBlobIds(tender_plan_attach_blob_ids),
                    quote_price_attach_blob_ids: transformToBlobIds(quoteFormValues?.bid_attachs),
                    quote_price: quoteFormValues?.total_quoted?.value,
                    quote_currency: quoteFormValues?.total_quoted?.currency,
                    bidding_supplier: {
                      bank_account: quoteFormValues?.channel_supplier_bank_dto?.bank_account,
                      bank_account_name: quoteFormValues?.channel_supplier_bank_dto?.bank_account_name,
                      bank_business_code: quoteFormValues?.channel_supplier_bank_dto?.bank_business_code,
                      invoice_belong_area: quoteFormValues?.invoice_belong_area,
                      is_oversea_invoice: !!quoteFormValues?.is_oversea_invoice,
                      invoice_type: quoteFormValues?.invoice_type,
                    },
                    tabs: quoteFormValues?.quote_detail,
                  }
                : {
                    id: isBlank(currentQuote.id) ? undefined : currentQuote.id,
                    tender_plan_attach_blob_ids: transformToBlobIds(tender_plan_attach_blob_ids),
                    quote_price_attach_blob_ids: transformToBlobIds(evaluationValues?.quote_price_attach_blob_ids),
                    quote_price: evaluationValues?.quote_price,
                    quote_currency: evaluationValues?.quote_currency,
                  },
              _.isUndefined,
            ),
          ],
        };

        await projectMove(
          {
            id: detail.id,
            pass: true,
            form: {
              ...data,
            },
            state: NodeEnum.AWAITING_SUBMISSION_BIDS,
            bidding_action: 'SUBMIT',
          },
          {
            menuKey: getListPathMenuKey(),
          },
        );
        showAlert({ type: 'success', message: '提交成功' });

        // 删除服务端暂存数据（仅结构化报价场景，不阻塞主流程）
        if (isStructured) {
          try {
            const { vendorId, tenantCode } = getVendorStorage();
            const code = `${vendorId}_${tenantCode}_${detail.id}`;
            await outsourceCommonDeleteTemporary({
              type: 'outsource_bidding_quote',
              code,
            });
          } catch (e) {
            console.warn('删除暂存数据失败', e);
          }
        }

        getDetail();
      }
    } finally {
      setLoading(false);
    }
  };

  const submit = useMemoizedFn(async (validData: any) => {
    const checked = await agreeCheckBoxRef.current?.getChecked();
    if (!checked) {
      return;
    }
    // structured=1 场景下先做一次表单预校验（避免表单错误在 handleSubmit 内部才暴露）
    if (isStructured) {
      const res = await validData();
      if (!res) {
        return;
      }
    }

    if (hasSubmitted) {
      Modal.confirm({
        title: '您已提交过比选方案，确定再次提交吗？',
        content: '再次提交将会覆盖上次提交内容',
        onOk: () => handleSubmit(validData),
      });
      return;
    }
    handleSubmit(validData);
  });

  return (
    <Template
      detail={detail}
      editFields={editFields}
      hasSubmitted={hasSubmitted}
      quoteData={quoteData}
      renderOpers={(validData: any) => {
        return (
          <>
            {canMove ? (
              <>
                <BiddingAgreeCheckbox
                  value={agree}
                  onChange={setAgree}
                  checkboxText="我已阅读并同意"
                  ref={agreeCheckBoxRef}
                />
                <Button onClick={() => submit(validData)} type="primary" loading={loading} disabled={loading}>
                  提交
                </Button>
              </>
            ) : null}
          </>
        );
      }}
    />
  );
};

export default AwaitingSubmissionBids;
